import { useCallback, useMemo, useRef, useState } from 'react';
import useInboxStore from '@/stores/InboxStore';
import { useHotkeys } from '@blueprintjs/core';
import { Cross2Icon, ReloadIcon } from '@radix-ui/react-icons';
import { useDebouncedCallback } from 'use-debounce';
import { useShallow } from 'zustand/react/shallow';

import { trackResetFilterButtonClicked } from '@/lib/analytics-event';

import KeyboardShortcut from '../common/keyboard/keyboard';
import TooltipComponent from '../common/tooltip/tooltip';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ConversationAgentPicker from './ConversationAgentPicker';
import ConversationChannelPicker from './ConversationChannelPicker';
import ConversationSearch from './ConversationSearch';
import ConversationSortPicker from './ConversationSortPicker';
import ConversationStatusPicker from './ConversationStatusPicker';
import ConversationTagPicker from './ConversationTagPicker';
import ReloadConversationComponent from './ReloadConversationComponent';

export default function InboxHeader() {
  const { selectedTags, selectedOwners, selectedChannels, isMultiSelectMode } =
    useInboxStore(
      useShallow((state) => ({
        selectedTags: state.selectedTags,
        selectedOwners: state.selectedOwners,
        selectedChannels: state.selectedChannels,
        isMultiSelectMode: state.isMultiSelectMode,
      }))
    );

  const { setSelectedTags, setSelectedOwners, setSelectedChannels } =
    useInboxStore(
      useShallow((state) => ({
        setSearchText: state.setSearchText,
        setSelectedTags: state.setSelectedTags,
        setSelectedOwners: state.setSelectedOwners,
        setSelectedChannels: state.setSelectedChannels,
      }))
    );

  const resetFilters = useCallback(
    (mode) => {
      setSelectedTags([]);
      setSelectedOwners([]);
      setSelectedChannels([]);
      trackResetFilterButtonClicked(mode);
    },
    [setSelectedTags, setSelectedOwners, setSelectedChannels]
  );

  const hotkeys = useMemo(
    () => [
      {
        combo: 'shift + R',
        global: true,
        group: 'Filtering',
        disabled: !selectedTags.length && !selectedOwners.length,
        label: 'Reset Tags and Owners',
        onKeyUp: () => resetFilters('keyboard'),
      },
    ],
    [resetFilters, selectedOwners, selectedTags]
  );

  const { handleKeyDown, handleKeyUp } = useHotkeys(hotkeys);

  return (
    <div className='space-y-3'>
      <ConversationSearch disabled={isMultiSelectMode} />
      <div className='flex flex-row items-center justify-between'>
        <div className='flex flex-row items-center justify-center space-x-4'>
          <ConversationStatusPicker disabled={isMultiSelectMode} />
          {(selectedTags.length > 0 ||
            selectedOwners.length > 0 ||
            selectedChannels.length > 0) && (
            <TooltipComponent
              content={
                <div className='flex space-x-2'>
                  <div>Reset</div>
                  <KeyboardShortcut keys={['shift', 'R']} />
                </div>
              }
            >
              <Button
                variant='ghost'
                onClick={() => resetFilters('mouse')}
                className='h-8 select-none px-2'
                disabled={isMultiSelectMode}
              >
                Reset
                <Cross2Icon className='ml-2 h-4 w-4' />
              </Button>
            </TooltipComponent>
          )}
        </div>
        <div className='flex flex-row items-center justify-center space-x-1'>
          <ReloadConversationComponent disabled={isMultiSelectMode} />
          <ConversationSortPicker disabled={isMultiSelectMode} />
        </div>
      </div>
      <div className='flex flex-row justify-between'>
        <div className='flex flex-row space-x-2'>
          <div>
            <ConversationTagPicker disabled={isMultiSelectMode} />
          </div>
          <div>
            <ConversationAgentPicker disabled={isMultiSelectMode} />
          </div>
          <div>
            <ConversationChannelPicker disabled={isMultiSelectMode} />
          </div>
        </div>
      </div>
    </div>
  );
}
